import * as React from 'react'
import { BaseFilterState, SingleFilterState} from './whole-graph/filters/helpers/FilterStates';
import { GDataContext, GDataContextManager } from './GDataContext';
import { GraphDataset } from './models/GraphDataset.interface';
import { GraphEdge } from './models/GraphEdge.interface';
import { GraphNode } from './models/GraphNode.interface';
import isMobile from 'is-mobile';
import { nodeChannelCountProperty } from './whole-graph/filters/elements/NodeChannelCountFilter';


type Props = {
  children: any,
  filters?: BaseFilterState[],
}


const GDataPrunedProvider = ({
  children,
  filters = [],
}: Props) => {
  const [prevFilters, setPrevFilters] = React.useState<BaseFilterState[]>([]);
  const [pruned, setPruned] = React.useState<GraphDataset>(null);
  const [isFirstTimeRender, setIsFirstTimeRender] = React.useState<boolean>(true);
  const baseGraphContext = React.useContext(GDataContext);
  

  if (!baseGraphContext) {
    throw new Error('PrunedGraphProvider requires WholeGraphProvider to provide data as a parent.');
  }

  function passesFilters(filters: BaseFilterState[], element: GraphNode | GraphEdge) {
    const results = filters.map(filter => {
      return filter.shouldInclude(element);
    });
    return results.filter(result => result === false).length === 0;
  }

  function pruneNodes(dataset: GraphDataset) {
    const nodeFilters = filters.filter(filter => filter.property.target === 'node');

    const nodes = [];
    const includedNodes = {};

    dataset.nodes.forEach(n => {
      if (passesFilters(nodeFilters, n)) {
        nodes.push(n);
        includedNodes[n.id] = true;
      }
    })

    const edges = dataset.edges.filter(e => {
      return includedNodes[e.source] && includedNodes[e.target];
    });

    return {
      nodes: nodes,
      edges: edges
    }
  }

  function pruneEdges(dataset: GraphDataset) {
    const edgeFilters = filters.filter(filter => filter.property.target === 'channel');

    const edges = dataset.edges.filter(edge => {
      return passesFilters(edgeFilters, edge);
    });

    const nodeEdgesCount = new Map<string, number>();

    function addOne(nodeId) {
      if (!nodeEdgesCount.has(nodeId)) {
        nodeEdgesCount.set(nodeId, 0);
      }
      const current = nodeEdgesCount.get(nodeId);
      nodeEdgesCount.set(nodeId, current + 1);
    }
    edges.forEach(e => {
      addOne(e.source);
      addOne(e.target);
    })

    const nodes = dataset.nodes.filter(n => {
      return nodeEdgesCount.has(n.id) && nodeEdgesCount.get(n.id) > 0;
    })

    return {
      edges: edges,
      nodes: nodes,
    }
  }

  function pruneGraph(dataset: GraphDataset): GraphDataset {
    // console.log('Filters', filters, dataset);
    dataset = pruneNodes(dataset);
    dataset = pruneEdges(dataset);
    return dataset;
  }

  function didFiltersChange(newStates: BaseFilterState[]) {
    if (newStates.length !== prevFilters.length) {
      return true;
    }
    for (let i = 0; i < newStates.length; i++) {
      const newState = newStates[i];
      const prevState = prevFilters[i];

      if (!newState.isEqual(prevState)) {
        return true;
      }
    }
    return false;
  }

  React.useEffect(() => {
    if (!baseGraphContext.dataset) {
      setPruned(undefined);
      return;
    }

    if (isMobile() && isFirstTimeRender) {
      filters = [
        ...filters,
        new SingleFilterState(nodeChannelCountProperty, 'gte', 5)
      ];
      setIsFirstTimeRender(false);
    }

    const sortedFitlers = filters.slice().sort((a, b) => a.property.key.localeCompare(b.property.key))
    const didChange = didFiltersChange(sortedFitlers);
   
    if (pruned && !didChange) {
      return;
    }
    // console.log('New filter states', sortedFitlers, 'old', prevFilters);
    setPrevFilters(sortedFitlers);
    const prunedGraph = pruneGraph(baseGraphContext.dataset);
    setPruned(prunedGraph as GraphDataset);
    
  }, [filters, baseGraphContext.dataset])

  return (
    <GDataContext.Provider value={new GDataContextManager(pruned, baseGraphContext.createdAt)}>
      {children}
    </GDataContext.Provider>
  );
};

export default GDataPrunedProvider;